import React, { useContext } from "react";
import { useFormContext } from "react-hook-form";
import { AuthContext } from "../../../../../../Authorization/AuthContext";
import { ObjectFieldTemplate } from "../../../../../../Submissions/Templates";
import { BuilderFieldWrapper } from "../../__styles__/SubmissionsBuilder";
import { SubmissionsBuilderContextInstance } from "../../context";
import {
  FieldActionsUpdaterProps,
  FieldContext,
  NonEnumInputField,
  SubmissionsBuilderFormDataStructure,
} from "../../types";
import { getNameFromId, withStopPropagation } from "../../utils";
import useFieldActionsPropsUpdater from "../useFieldActionsPropsUpdater";
import FieldPreviewFooter from "./FieldPreviewFooter";

const ObjectFieldTemplatePreview = (
  props: Parameters<typeof ObjectFieldTemplate>[0],
) => {
  const formMethods = useFormContext<SubmissionsBuilderFormDataStructure>();
  const { fieldContextsByName, fieldActionsPropsByPath } = useContext(
    SubmissionsBuilderContextInstance,
  );

  const id = props.idSchema.$id;
  const name = getNameFromId(id);
  const fieldContext = fieldContextsByName[id];

  if (id === "root") {
    useFieldActionsPropsUpdater(formMethods, "inputs");
    return <ObjectFieldTemplate {...props} />;
  }

  if (!fieldContext) {
    return null;
  }

  const fieldProps = fieldActionsPropsByPath[fieldContext.fieldPath];

  if (!fieldProps) {
    return null;
  }

  return (
    <GroupInputPreview
      fieldContext={fieldContext}
      fieldProps={fieldProps}
      name={name}
      {...props}
    />
  );
};

const GroupInputPreview = ({
  fieldContext,
  fieldProps,
  name,
  ...props
}: {
  fieldContext: FieldContext;
  fieldProps: FieldActionsUpdaterProps;
  name: string;
} & Parameters<typeof ObjectFieldTemplate>[0]) => {
  const formMethods = useFormContext<SubmissionsBuilderFormDataStructure>();
  const { account } = useContext(AuthContext);
  const { selectedField } = useContext(SubmissionsBuilderContextInstance);

  useFieldActionsPropsUpdater(
    formMethods,
    `${fieldContext.fieldPath}.properties` as NonEnumInputField,
  );

  const { selectField } = fieldProps;

  const isSelected = selectedField?.name === name;

  return (
    <BuilderFieldWrapper
      onClick={withStopPropagation(selectField)}
      isSelected={isSelected}
      isGroupWrapper
      role="group"
      aria-selected={isSelected}
      aria-label="Field group"
    >
      <ObjectFieldTemplate {...props} isSubmissionBuilder />
      <FieldPreviewFooter
        fieldPath={fieldContext.fieldPath}
        {...fieldProps}
        isGroup
        userGroups={account?.userGroups ?? []}
      />
    </BuilderFieldWrapper>
  );
};

export default ObjectFieldTemplatePreview;
